import React, {ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import './index.scss';

import iconCancel from '../../assets/images/icons/scenario/cancel.png';
import iconSave from '../../assets/images/icons/scenario/save.png';
import iconTime from '../../assets/images/icons/scenario/time.png';
import iconSunrise from '../../assets/images/icons/scenario/sunrise.png';
import iconSunset from '../../assets/images/icons/scenario/sunset.png';
import iconScenario from '../../assets/images/icons/scenario/scenario.png';
import {useKeyboard} from '../../hooks/keyboard';
import {
    ScenarioModel,
    ScenarioTriggerModel,
    TimeTriggerSettingsModel,
    TimeTriggerType,
} from '../../store/control/state';
import {useDispatch, useSelector} from 'react-redux';
import {toggleScenarioTriggerScreenAction} from '../../store/ui/actions';
import {useRPC} from '../../hooks/rpc';
import {useMessage} from '../../hooks/message';
import {onClickHandler} from '../../utils/onclick';
import {useLocalization} from '../../hooks/localization';
import { useOutsideDetector } from '../../hooks/outside-detector';

const format = (str: string) => {
    if (str.length === 1) {
        return "" + str[0] + "-:--"
    }
    if (str.length === 2) {
        return "" + str[0] + str[1] + ":--"
    }
    if (str.length === 3) {
        return "" + str[0] + str[1] + ":" + str[2] + "-"
    }
    if (str.length === 4) {
        return "" + str[0] + str[1] + ":" + str[2] + str[3]
    }

    return "--:--"
};

const formatMinute = (str: string) => {
    if (str.length === 1) {
        return "" + str[0] + "--"
    }
    if (str.length === 2) {
        return "" + str[0] + str[1] + "-"
    }
    if (str.length === 3) {
        return "" + str[0] + str[1] + str[2]
    }

    return "---"
}

const valid = (str: string, fullCheck = false) => {
    if (fullCheck && (!str || str.length !== 4)) {
        return false;
    }

    if (!str) {
        return true;
    }

    const hourFirst = parseInt(str[0]);
    if (hourFirst > 2) {
        return false;
    }

    if (str.length > 1) {
        const hour = parseInt(str[0] + str[1]);
        if (hour > 23) {
            return false;
        }
    }

    if (str.length > 2) {
        const minute = parseInt(str[2]);
        if (minute > 5) {
            return false;
        }
    }

    return true;
};

const validForMinute = (str: string, fullCheck = false) => {

    if (!str) {
        return true
    }

    return true
}

type AllDaysType = {
    week: number
    day: number
}
function getAllDays(year: number, month: number) {
    const lastDay = new Date(year, month, 0).getDate()
    const days = []
    for (let day = 1; day <= lastDay; day++) {
        days.push({
            day: new Date(year, month - 1, day).getDate(),
            week: new Date(year, month - 1, day).getDay()
        })
    }
    return days as AllDaysType[]
}
const date = new Date()

export const ScenarioTriggerCreateScreen = memo(function ScenarioTriggerCreateScreen() {
    const {t} = useLocalization();
    const scenarios = useSelector(state => state.control.scenarioList);
    const selectedTrigger = useSelector(state => state.ui.triggerScreen.selectedTrigger);
    const suntime = useSelector(state => state.control.suntime)

    const [openPopup, setOpenPopup] = useState(false);
    const outsideClick = useOutsideDetector(openPopup, () =>
      setOpenPopup(false)
    );

    const MONTHS = useMemo(
      () => [
        {
          value: 1,
          name: t("energy_page.january"),
        },
        {
          value: 2,
          name: t("energy_page.february"),
        },
        {
          value: 3,
          name: t("energy_page.march"),
        },
        {
          value: 4,
          name: t("energy_page.april"),
        },
        {
          value: 5,
          name: t("energy_page.may"),
        },
        {
          value: 6,
          name: t("energy_page.june"),
        },
        {
          value: 7,
          name: t("energy_page.july"),
        },
        {
          value: 8,
          name: t("energy_page.august"),
        },
        {
          value: 9,
          name: t("energy_page.september"),
        },
        {
          value: 10,
          name: t("energy_page.october"),
        },
        {
          value: 11,
          name: t("energy_page.november"),
        },
        {
          value: 12,
          name: t("energy_page.december"),
        },
      ],
      [t]
    );


    const dispatch = useDispatch();
    const {failure} = useMessage();
    const [rpc] = useRPC();

    const inputRef = useRef<HTMLInputElement>();
    const timeRef = useRef<HTMLInputElement>();
    const diffRef = useRef<HTMLInputElement>();

    const [triggerName, setTriggerName] = useState('');
    const [timeStr, setTimeStrRaw] = useState('');
    const [diffStr, setDiffStrRaw] = useState('');
    const [reverseTrigger, setReverseTrigger] = useState(false);

    const [scenarioId, setTriggerId] = useState('');
    const [isAdvanced, setIsAdvanced] = useState(false)

    const setTimeStr = useCallback((value: string) => {
        if (value) {
            value = value.replace(' ', '');
            if (value.length > 4) {
                value = value.substr(0, 4);
            }
        }
        setTimeStrRaw(value);
    }, []);

    const setDiffStr = useCallback((value: string) => {
        if (value) {
            value = value.replace(' ', '');
            if (value.length > 3) {
                value = value.substr(0, 3);
            }
        }
        setDiffStrRaw(value);
    }, []);

    const [setActiveInput, renderKeyboard] = useKeyboard();
    const [setActiveNumberInput, renderNumpad] = useKeyboard('numpad', true);

    const [triggerType, setTriggerType] = useState<TimeTriggerType>('exact');
    const [isBefore, setIsBefore] = useState(false);
    const [selectedScenario, setSelectedScenario] = useState<ScenarioModel>(null);

    const beforeAfterClicked = useCallback((isBeforeClicked: boolean) => {
        setIsBefore(isBeforeClicked);
        if (triggerType === 'exact') {
            setTriggerType('sunrise');
        }
    }, [triggerType]);

    const astronomicalTimeClicked = useCallback((isBeginningOfTheDay: boolean) => {
        if (triggerType === 'exact') {
            setIsBefore(true);
        }
        setTriggerType(isBeginningOfTheDay ? 'sunrise' : 'sunset');
    }, [triggerType]);

    const [selectedDays, setSelectedDays] = useState([true, true, true, true, true, true, true]);

    const dayClicked = useCallback(
      (day: number) => {
        if (isAdvanced) {
          return;
        }
        const days = [...selectedDays];
        days[day] = !days[day];
        setSelectedDays(days);
      },
      [selectedDays, isAdvanced]
    );

    const openAdvancedOptionsPopup = useCallback(() => {
        setOpenPopup(true)
        setIsAdvanced(true)
        setSelectedDays([false, false, false, false, false, false, false])
    }, [])

    const close = useCallback(() => {
        dispatch(toggleScenarioTriggerScreenAction(null));
    }, [dispatch]);

    const onFocus = useCallback(() => {
        setActiveInput([inputRef, triggerName, setTriggerName]);
    }, [setActiveInput, triggerName]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setTriggerName(e.target.value), []);

    const onFocusTime = useCallback(() => {
        setActiveNumberInput([timeRef, timeStr, setTimeStr]);
    }, [setTimeStr, setActiveNumberInput, timeStr]);

    const onBlurNumber = useCallback(() => {
        setActiveNumberInput(null);
    }, [setActiveNumberInput]);

    const onChangeTime = useCallback((e: ChangeEvent<HTMLInputElement>) => setTimeStr(e.target.value), [setTimeStr]);

    const onFocusDiff = useCallback(() => {
        setActiveNumberInput([diffRef, diffStr, setDiffStr]);
    }, [setDiffStr, setActiveNumberInput, diffStr]);

    const onChangeDiff = useCallback((e: ChangeEvent<HTMLInputElement>) => setDiffStr(e.target.value), [setDiffStr]);

    const time = useMemo(() => format(timeStr), [timeStr]);
    const diff = useMemo(() => formatMinute(diffStr), [diffStr])


    useEffect(() => {
        if (selectedTrigger) {
            setTriggerName(selectedTrigger.name);
            setTriggerId(selectedTrigger.id);
            setTriggerType(selectedTrigger.settings.type);
            setReverseTrigger(selectedTrigger.is_reverse_trigger)
            setIsAdvanced(selectedTrigger.settings?.is_advanced)

            if (selectedTrigger.settings?.is_advanced) {
                let list: any = {}
                for (let i = 0; i <= 11; i++) {
                    list[i + 1] = selectedTrigger.settings.adv_day_list[i]?.days
                }
                setSelectedAllDays(list)
            }

            const selectedDays = [false, false, false, false, false, false, false];
            for (const day of selectedTrigger.settings.day_list) {
                selectedDays[day] = true;
            }
            setSelectedDays(selectedDays);
            if (scenarios) {
                for (const scenario of scenarios) {
                    if (scenario.id === selectedTrigger.scenario_id) {
                        setSelectedScenario(scenario);
                        break;
                    }
                }
            }

            let second = selectedTrigger.settings.delay_in_seconds;
            if (second < 0) {
                second *= -1;
                setIsBefore(true);
            }

            const hour = Math.floor(second / 3600);
            const min = Math.floor((second % 3600) / 60);
            let hourStr = hour.toFixed(0);
            let minStr = min.toFixed(0);
            if (hourStr.length < 2) {
                hourStr = '0' + hourStr;
                if (hourStr.length < 2) {
                    hourStr = '0' + hourStr;
                }
            }

            if (minStr.length < 2) {
                minStr = '0' + minStr;
                if (minStr.length < 2) {
                    minStr = '0' + minStr;
                }
            }

            if (selectedTrigger.settings.type === 'exact') {
                setTimeStr(hourStr + minStr);
            } else {
                setDiffStr(String(second / 60))
            }
        }
    }, [scenarios, selectedTrigger, setDiffStr, setTimeStr]);

    const [selectedMonth, setSelectedMonth] = useState(1)
    const selectedDate = getAllDays(date.getFullYear(), selectedMonth)

    const selectedDayFilter = useCallback(
        (month: number) => getAllDays(date.getFullYear(), month).map(day => day.day),
        []
    )

    const selectedWeekFilter = useCallback((month: number) => getAllDays(date.getFullYear(), month).map(day => day), [])
    const [selectedAllDays, setSelectedAllDays] = useState({
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
        6: [],
        7: [],
        8: [],
        9: [],
        10: [],
        11: [],
        12: []
    })

    const [dayCheck, setDayCheck] = useState({
        0: false,
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
        6: false
    })


    const [checkFilteredWeeks, setCheckFilteredWeeks] = useState({});
    useEffect(() => {
      let allYear = {};
      let allWeeks = {};
      let selectedCheckDays = {};
      for (let i = 1; i <= 12; i++) {
        allYear[i] = getAllDays(date.getFullYear(), i);
        allWeeks[i] = selectedAllDays[i]?.filter(
          (item: number) => allYear[i][item]
        );
        selectedCheckDays[i] = allYear[i]?.filter((item) =>
          allWeeks[i]?.includes(item.day)
        );
      }
      setCheckFilteredWeeks(selectedCheckDays);
    }, [selectedAllDays, dayCheck]);

    useEffect(() => {
      let newArr: any = {};
      for (let i = 1; i <= 12; i++) {
        for (let j = 0; j <= 6; j++) {
          newArr[j] = checkFilteredWeeks[i]?.map((r) => r?.week)?.includes(j);
        }
      }
      const shouldUpdate = Object.keys(newArr).some(
        (key) => newArr[key] !== dayCheck[key]
      );
      if (shouldUpdate) {
        setDayCheck(newArr);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkFilteredWeeks]);

    const onClickSelectMonth = useCallback(
      (month: number) => setSelectedMonth(month),
      []
    );

    const onClickMonthOptions = useCallback(
      (month: number) => {
        setSelectedAllDays((prevState) => {
          const updatedDays = [...prevState?.[month]];
          const isMonthSelected = updatedDays.length > 0;
          const newDays = isMonthSelected ? [] : selectedDayFilter(month);
          return {
            ...prevState,
            [month]: newDays,
          };
        });
      },
      [selectedDayFilter]
    );

    const onClickSelectAll = useCallback(() => {
      let allList: any = {};
      for (let i = 1; i <= 12; i++) {
        allList[i] = selectedDayFilter(i);
      }
      setSelectedAllDays(allList);
    }, [selectedDayFilter]);

    const onClickRemoveAll = useCallback(() => {
      setSelectedAllDays({
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
        6: [],
        7: [],
        8: [],
        9: [],
        10: [],
        11: [],
        12: [],
      });
    }, []);

    const customizedNewDayList = useMemo(() => {
      const list = [];
      for (let i = 1; i < 13; i++) {
        list.push({
          days: selectedAllDays[i],
        });
      }
      return list;
    }, [selectedAllDays]);

    const onClickDay = useCallback(
      (day: number) => {
        let allList: any = { ...selectedAllDays };
        for (let i = 1; i <= 12; i++) {
          const currentSelection = allList[i];
          const newSelection = selectedWeekFilter(i)
            .filter((value) => value.week === day)
            .map((value) => value.day);

          if (
            currentSelection.some((selectedDay: number) =>
              newSelection.includes(selectedDay)
            )
          ) {
            allList[i] = currentSelection.filter(
              (selectedDay: number) => !newSelection.includes(selectedDay)
            );
          } else {
            allList[i] = Array.from(
              new Set([...currentSelection, ...newSelection])
            );
          }
        }
        setSelectedAllDays(allList);
      },
      [selectedWeekFilter, selectedAllDays]
    );

    const onClickMidWeek = useCallback(() => {
      let allList: any = {};
      for (let i = 1; i <= 12; i++) {
        allList[i] = selectedWeekFilter(i)
          .filter(
            (value) =>
              value.week === 1 ||
              value.week === 2 ||
              value.week === 3 ||
              value.week === 4 ||
              value.week === 5
          )
          .map((value) => value.day);
      }
      setSelectedAllDays(allList);
    }, [selectedWeekFilter]);

    const onClickWeekend = useCallback(() => {
      let allList: any = {};
      for (let i = 1; i <= 12; i++) {
        allList[i] = selectedWeekFilter(i)
          .filter((value) => value.week === 6 || value.week === 0)
          .map((value) => value.day);
      }
      setSelectedAllDays(allList);
    }, [selectedWeekFilter]);

    const onClickSelectDays = useCallback(
      (day: number) => {
        setSelectedAllDays((prevState) => {
          const updatedDays = [...prevState?.[selectedMonth]];
          if (updatedDays.includes(day)) {
            const index = updatedDays.indexOf(day);
            updatedDays.splice(index, 1);
          } else {
            updatedDays.push(day);
          }
          return {
            ...prevState,
            [selectedMonth]: updatedDays,
          };
        });
      },
      [selectedMonth]
    );

    const onClickSetAdvanced = useCallback(() => {
      setIsAdvanced((p) => !p);
      setSelectedDays([false, false, false, false, false, false, false]);
    }, []);


    const save = useCallback(() => {
        const dayList = selectedDays.map((selected, index) => selected ? index : -1).filter(index => index !== -1);
        if ((triggerType === 'exact' && !valid(timeStr, true)) || (triggerType !== 'exact' && !validForMinute(diffStr, true))) {
            failure(t('pls_enter_a_valid_time'));
            return;
        }

        if (!triggerName || triggerName.length < 3) {
            failure(t('pls_enter_a_valid_name'));
            return;
        }

        if (isAdvanced) {
            const fullList = []
            for (let i = 1; i <= 12; i++) {
                fullList.push(...selectedAllDays[i])
            }
            if (fullList.length === 0) {
                failure(t("pls_select_at_least_one_day"))
                return
            }
        }

        if (dayList.length === 0 && !isAdvanced) {
            failure(t("pls_select_at_least_one_day"))
            return
        }

        if (!selectedScenario) {
            failure(t('pls_select_a_scenario'));
            return;
        }

        let delayInSeconds;
        if (triggerType === 'exact') {
            delayInSeconds = (parseInt(timeStr[0] + timeStr[1]) * 60 + parseInt(timeStr[2] + timeStr[3])) * 60;
        } else {


            //delayInSeconds = (parseInt(diffStr[0] + diffStr[1]) * 60 + parseInt(diffStr[2] + diffStr[3])) * 60;
            delayInSeconds = (parseInt(diffStr[0] + diffStr[1] + diffStr[2]) * 60)

            console.log(delayInSeconds)
            if (isBefore) {
                delayInSeconds *= -1;
            }



        }

        const settings: TimeTriggerSettingsModel = {
            day_list: dayList,
            type: triggerType,
            delay_in_seconds: delayInSeconds,
            is_advanced: isAdvanced,
            adv_day_list: customizedNewDayList
        }

        const trigger: ScenarioTriggerModel = {
            id: scenarioId,
            name: triggerName,
            scenario_id: selectedScenario.id,
            settings: settings,
            is_passive: selectedTrigger?.is_passive || false,
            skip_count: selectedTrigger?.skip_count || 0,
            is_reverse_trigger: reverseTrigger
        }

        rpc.updateScenarioTrigger(trigger).then(isSucceeded => {
            if (isSucceeded) {
                close();
            }
        });

    }, [t, triggerType, timeStr, diffStr, triggerName, customizedNewDayList, selectedAllDays, isAdvanced, reverseTrigger, selectedDays, selectedScenario, scenarioId, rpc, failure, isBefore, close, selectedTrigger]);

    return (
        <>
            {openPopup && (
                <div className="trigger-overlay">
                    <div ref={outsideClick} className="trigger-date-container">
                        <div className="trigger-date">
                            <div className="left-side">
                                {MONTHS.map(month => (
                                    <span
                                        className={`months ${selectedMonth === month?.value && "active"}`}
                                        {...onClickHandler(() => onClickSelectMonth(month?.value))}
                                        key={month?.value}
                                    >
                                        {month?.name}
                                        <span
                                            className={`${selectedAllDays[month?.value]?.length > 0 ? "active" : ""}`}
                                            {...onClickHandler(() => onClickMonthOptions(month?.value))}
                                        />
                                    </span>
                                ))}
                            </div>
                            <div className="right-side">
                                <div className="week-days">
                                    <span>{t("authority.monday")}</span>
                                    <span>{t("authority.tuesday")}</span>
                                    <span>{t("authority.wednesday")}</span>
                                    <span>{t("authority.thursday")}</span>
                                    <span>{t("authority.friday")}</span>
                                    <span>{t("authority.saturday")}</span>
                                    <span>{t("authority.sunday")}</span>
                                </div>
                                <div className="all-days">
                                    {renderIf(selectedDate[0]?.week === 2)(() => (
                                        <>
                                            <span className="_none" />
                                        </>
                                    ))}
                                    {renderIf(selectedDate[0]?.week === 3)(() => (
                                        <>
                                            <span className="_none" />
                                            <span className="_none" />
                                        </>
                                    ))}
                                    {renderIf(selectedDate[0]?.week === 4)(() => (
                                        <>
                                            <span className="_none" />
                                            <span className="_none" />
                                            <span className="_none" />
                                        </>
                                    ))}
                                    {renderIf(selectedDate[0]?.week === 5)(() => (
                                        <>
                                            <span className="_none" />
                                            <span className="_none" />
                                            <span className="_none" />
                                            <span className="_none" />
                                        </>
                                    ))}
                                    {renderIf(selectedDate[0]?.week === 6)(() => (
                                        <>
                                            <span className="_none" />
                                            <span className="_none" />
                                            <span className="_none" />
                                            <span className="_none" />
                                            <span className="_none" />
                                        </>
                                    ))}
                                    {renderIf(selectedDate[0]?.week === 0)(() => (
                                        <>
                                            <span className="_none" />
                                            <span className="_none" />
                                            <span className="_none" />
                                            <span className="_none" />
                                            <span className="_none" />
                                            <span className="_none" />
                                        </>
                                    ))}
                                    {selectedDate.map(days => (
                                        <span
                                            className={`${
                                                selectedAllDays[selectedMonth]?.includes(days?.day) ? "active" : "passive"
                                            }`}
                                            key={days?.day}
                                            onClick={() => onClickSelectDays(days?.day)}
                                        >
                                            {days?.day}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="trigger-action-container">
                            <div className="trigger-action-box">
                                <div className="trigger-action-button green" {...onClickHandler(onClickSelectAll)}>
                                    {t("select_all")}
                                </div>
                                <div className="trigger-action-button red" {...onClickHandler(onClickRemoveAll)}>
                                    {t("remove_all")}
                                </div>
                                <div className="trigger-action-button blue" {...onClickHandler(onClickMidWeek)}>
                                    {t("mid_week")}
                                </div>
                                <div className="trigger-action-button yellow" {...onClickHandler(onClickWeekend)}>
                                    {t("weekend")}
                                </div>
                            </div>
                            <div className="trigger-action-right-side">
                                <div
                                    className="advanced-options-selectbox-container"
                                    {...onClickHandler(() => onClickDay(1))}
                                >
                                    <span className={dayCheck[1] ? "checked" : ""} />
                                    <div className="_selectbox-text">{t("authority.monday")}</div>
                                </div>
                                <div
                                    className="advanced-options-selectbox-container"
                                    {...onClickHandler(() => onClickDay(2))}
                                >
                                    <span className={dayCheck[2] ? "checked" : ""} />
                                    <div className="_selectbox-text">{t("authority.tuesday")}</div>
                                </div>
                                <div
                                    className="advanced-options-selectbox-container"
                                    {...onClickHandler(() => onClickDay(3))}
                                >
                                    <span className={dayCheck[3] ? "checked" : ""} />
                                    <div className="_selectbox-text">{t("authority.wednesday")}</div>
                                </div>
                                <div
                                    className="advanced-options-selectbox-container"
                                    {...onClickHandler(() => onClickDay(4))}
                                >
                                    <span className={dayCheck[4] ? "checked" : ""} />
                                    <div className="_selectbox-text">{t("authority.thursday")}</div>
                                </div>
                                <div
                                    className="advanced-options-selectbox-container"
                                    {...onClickHandler(() => onClickDay(5))}
                                >
                                    <span className={dayCheck[5] ? "checked" : ""} />
                                    <div className="_selectbox-text">{t("authority.friday")}</div>
                                </div>
                                <div
                                    className="advanced-options-selectbox-container"
                                    {...onClickHandler(() => onClickDay(6))}
                                >
                                    <span className={dayCheck[6] ? "checked" : ""} />
                                    <div className="_selectbox-text">{t("authority.saturday")}</div>
                                </div>
                                <div
                                    className="advanced-options-selectbox-container"
                                    {...onClickHandler(() => onClickDay(0))}
                                >
                                    <span className={dayCheck[0] ? "checked" : ""} />
                                    <div className="_selectbox-text">{t("authority.sunday")}</div>
                                </div>
                            </div>
                        </div>

                        <div className="trigger-close-button-container" {...onClickHandler(() => setOpenPopup(false))}>
                            <div className="trigger-close-button">{t("ok")}</div>
                        </div>
                    </div>
                </div>
            )}
            <div className="content-page">
                <div className="content-divider top">
                    <div className="content-divider left">
                        <div className="content-top">
                            <div className="content-wrapper">
                                <div className="action-row">
                                    <div className="action" {...onClickHandler(close)}>
                                        <img src={iconCancel} alt="" />
                                        <span>{t("cancel")}</span>
                                    </div>
                                    <input
                                        maxLength={20}
                                        placeholder={t("enter_trigger_name")}
                                        ref={inputRef}
                                        onChange={onChange}
                                        className="highlighted"
                                        onFocus={onFocus}
                                        onBlur={onBlur}
                                        value={triggerName}
                                    />
                                    <div className="action" {...onClickHandler(save)}>
                                        <img src={iconSave} alt="" />
                                        <span>{t("save")}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="content-center">
                            <div className="child">
                                <div className="content-wrapper">
                                    <input
                                        style={{border: 0, background: "transparent", width: 0, height: 0}}
                                        maxLength={4}
                                        type="number"
                                        ref={timeRef}
                                        onChange={onChangeTime}
                                        onFocus={onFocusTime}
                                        onBlur={onBlurNumber}
                                        value={timeStr}
                                    />
                                    <span
                                        {...onClickHandler(() => timeRef.current.focus())}
                                        className={valid(timeStr) ? "" : "invalid"}
                                    >
                                        {time}
                                    </span>
                                    <div
                                        className={"check-box" + (triggerType === "exact" ? " checked" : "")}
                                        style={{borderRadius: "50%"}}
                                        {...onClickHandler(() => setTriggerType("exact"))}
                                    />
                                    <img src={iconTime} alt="" />
                                </div>
                            </div>
                            <div className="parent-child">
                                <div
                                    className="child"
                                    style={{
                                        width: "100%",
                                        minHeight: "142px",
                                        height: "100%",
                                        marginBottom: "3px",
                                        alignItems: "center"
                                    }}
                                >
                                    <div className="content-wrapper">
                                        <input
                                            style={{border: 0, background: "transparent", width: 0, height: 0}}
                                            maxLength={3}
                                            ref={diffRef}
                                            type="number"
                                            onChange={onChangeDiff}
                                            onFocus={onFocusDiff}
                                            onBlur={onBlurNumber}
                                            value={diffStr}
                                        />
                                        <span
                                            {...onClickHandler(() => diffRef.current.focus())}
                                            className={validForMinute(diffStr) ? "" : "invalid"}
                                        >
                                            {diff}'
                                        </span>
                                        <div className="trigger-special-row">
                                            <span>{t("before")}</span>
                                            <span>{t("after")}</span>
                                        </div>
                                        <div className="trigger-special-row">
                                            <div
                                                className={"check-box-circle" + (isBefore ? " _checked" : "")}
                                                {...onClickHandler(() => beforeAfterClicked(true))}
                                            />
                                            <div
                                                className={"check-box-circle" + (!isBefore ? " _checked" : "")}
                                                {...onClickHandler(() => beforeAfterClicked(false))}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="child"
                                    style={{
                                        width: "100%",
                                        maxHeight: "150px",
                                        height: "100%"
                                    }}
                                >
                                    <div className="content-wrapper">
                                        <div className="trigger-special-row">
                                            <img src={iconSunrise} alt="" />
                                            <img src={iconSunset} alt="" />
                                        </div>
                                        <div className="trigger-special-row">
                                            <small style={{color: "#fff"}}>{suntime.sunrise.slice(0, 5)}</small>
                                            <small style={{color: "#fff"}}>{suntime.sunset.slice(0, 5)}</small>
                                        </div>
                                        <div className="trigger-special-row">
                                            <div
                                                className={
                                                    "check-box-circle" + (triggerType === "sunrise" ? " _checked" : "")
                                                }
                                                {...onClickHandler(() => astronomicalTimeClicked(true))}
                                            />
                                            <div
                                                className={
                                                    "check-box-circle" + (triggerType === "sunset" ? " _checked" : "")
                                                }
                                                {...onClickHandler(() => astronomicalTimeClicked(false))}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="content-bottom">
                            <div className="content-wrapper">
                                <div className="content-line">
                                    <div className="day-container" {...onClickHandler(() => dayClicked(1))}>
                                        <div className={"check-box" + (selectedDays[1] ? " checked" : "")} />
                                        <span>{t("monday")}</span>
                                    </div>
                                    <div className="day-container" {...onClickHandler(() => dayClicked(2))}>
                                        <div className={"check-box" + (selectedDays[2] ? " checked" : "")} />
                                        <span>{t("tuesday")}</span>
                                    </div>
                                    <div className="day-container" {...onClickHandler(() => dayClicked(3))}>
                                        <div className={"check-box" + (selectedDays[3] ? " checked" : "")} />
                                        <span>{t("wednesday")}</span>
                                    </div>
                                    <div className="day-container" {...onClickHandler(() => dayClicked(4))}>
                                        <div className={"check-box" + (selectedDays[4] ? " checked" : "")} />
                                        <span>{t("thursday")}</span>
                                    </div>
                                </div>
                                <div className="content-line">
                                    <div className="day-container" {...onClickHandler(() => dayClicked(5))}>
                                        <div className={"check-box" + (selectedDays[5] ? " checked" : "")} />
                                        <span>{t("friday")}</span>
                                    </div>
                                    <div className="day-container" {...onClickHandler(() => dayClicked(6))}>
                                        <div className={"check-box" + (selectedDays[6] ? " checked" : "")} />
                                        <span>{t("saturday")}</span>
                                    </div>
                                    <div className="day-container" {...onClickHandler(() => dayClicked(0))}>
                                        <div className={"check-box" + (selectedDays[0] ? " checked" : "")} />
                                        <span>{t("sunday")}</span>
                                    </div>
                                    <div className="day-container">
                                        <div
                                            className={"check-box" + (isAdvanced ? " checked" : "")}
                                            {...onClickHandler(onClickSetAdvanced)}
                                            style={{borderRadius: "50%"}}
                                        />
                                        <span {...onClickHandler(openAdvancedOptionsPopup)}>{t("advanced")}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="content-divider right">
                        {scenarios.map(scenario => (
                            <div className="content-wrapper">
                                <div className="left-container">
                                    <img src={iconScenario} alt="" />
                                    <span>{scenario.name}</span>
                                </div>
                                <div className="checkbox-opt-wrapper">
                                    {renderIf(scenario?.is_reversible)(() => (
                                        <div
                                            {...onClickHandler(() => {
                                                setSelectedScenario(scenario)
                                                setReverseTrigger(true)
                                            })}
                                            className={
                                                "check-box triangle-left" +
                                                (reverseTrigger && selectedScenario === scenario
                                                    ? " checked-triangle-left"
                                                    : "")
                                            }
                                        />
                                    ))}

                                    <div
                                        className={
                                            "check-box triangle-right" +
                                            (selectedScenario === scenario && !reverseTrigger
                                                ? " checked-triangle-right"
                                                : "")
                                        }
                                        {...onClickHandler(() => {
                                            setSelectedScenario(scenario)
                                            setReverseTrigger(false)
                                        })}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {renderKeyboard()}
            {renderNumpad()}
        </>
    )
});
